import { supabase } from './supabaseClient';
import type { InvoiceTemplateData, InvoiceRequestData, InvoiceLineItem, InvoicePayment } from '@/components/invoices/InvoiceDocument';
import type { DeliveryChallanTemplateData } from '@/components/invoices/DeliveryChallanDocument';
import type { InvoiceRefund } from '@/components/invoices/RefundWatermark';
import type { InvoiceAssets } from '@/types/invoice';
import { buildInvoicePaymentInfo } from './paymentStatus';
import { sortProducts } from './productOrder';

const CORE_URL = import.meta.env.VITE_CORE_URL ?? '';
const CORE_ANON = import.meta.env.VITE_CORE_ANON ?? '';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

interface InvoiceRequestRow {
  doc_type: string;
  doc_number: string | null;
  status: string;
  recipient_name: string | null;
  recipient_email: string | null;
  company_name: string | null;
  business_number: string | null;
  company_address: string | null;
  company_contact: string | null;
  issue_date: string | null;
  due_date: string | null;
  currency: string | null;
  line_items: InvoiceLineItem[] | null;
  subtotal: number | null;
  discount_bdt: number | null;
  shipping_bdt: number | null;
  tax_rate: number | null;
  tax_amount: number | null;
  total_amount: number | null;
  notes: string | null;
  payment_terms: string | null;
}

interface InvoiceTemplateRow {
  issuer_name: string | null;
  issuer_business_number: string | null;
  office_address: string | null;
  issuer_address: string | null;
  issuer_contact: string | null;
  issuer_email: string | null;
  issuer_website: string | null;
  legal_entity_name: string | null;
  trade_license_no: string | null;
  concern_line: string | null;
  social_url: string | null;
  show_vat_amount: boolean | null;
  default_tax_rate: number | null;
  invoice_text_overrides: Record<string, string> | null;
  challan_authorized_person_name: string | null;
  challan_authorized_person_designation: string | null;
  challan_text_overrides: Record<string, string> | null;
}

const INVOICE_COLUMNS =
  'doc_type, doc_number, status, recipient_name, recipient_email, company_name, business_number, company_address, company_contact, issue_date, due_date, currency, line_items, subtotal, discount_bdt, shipping_bdt, tax_rate, tax_amount, total_amount, notes, payment_terms';

// Settings → Invoice Identity is the source of truth once filled in — this
// only fills the gap if that row is still empty. Values match the same
// registered-entity details already published on the live About page
// (src/i18n/locales/en.ts, 'about.company.*') so the invoice isn't blank
// while nobody's typed them into Settings yet.
const DEFAULT_LEGAL_IDENTITY = {
  issuer_address: '2/2-A, South Begunbari, Tejgaon I/A, Dhaka-1208, Bangladesh',
  issuer_business_number: '009212412-0108',
  trade_license_no: 'TRAD/DNCC/027028/2025',
  legal_entity_name: 'Sol Grace Construction BD. Ltd.',
};

export interface InvoiceForOrder {
  status: string;
  request: InvoiceRequestData;
  template: InvoiceTemplateData;
  challanAuthorizedPersonName: string | null;
  challanAuthorizedPersonDesignation: string | null;
  challanTextOverrides: Record<string, string> | null;
}

// Physical units scanned in at sale time aren't stored on the invoice
// itself — matched here by sku against whatever got linked to this order
// (core_product_authenticity_codes.sold_order_id) so the printed line
// shows exactly which serials it corresponds to. Older invoices (from
// before line_items carried a sku) just won't match anything, harmlessly.
async function attachSerials(orderId: string, lineItems: InvoiceLineItem[]): Promise<InvoiceLineItem[]> {
  const { data: soldUnits } = await supabase.from('core_product_authenticity_codes').select('sku, inventory_code').eq('sold_order_id', orderId);
  const serialsBySku: Record<string, string[]> = {};
  for (const unit of (soldUnits as { sku: string; inventory_code: string }[]) ?? []) {
    (serialsBySku[unit.sku] ??= []).push(unit.inventory_code);
  }
  return lineItems.map((item) => (item.sku && serialsBySku[item.sku]?.length ? { ...item, serials: serialsBySku[item.sku] } : item));
}

interface BundleComponent {
  sku: string;
  name_en: string | null;
  msrp_bdt: number;
  qty_per_bundle: number;
}

// Bundle SKUs (e.g. SET-3STEP) are sold and stored on the order as one
// line at the bundle's own discounted price. For the printed/PDF invoice
// we want that line expanded into its real components at their normal
// MRP, with the difference surfaced as an explicit "Bundle Discount" —
// so a customer sees the ৳500 they saved instead of one opaque line.
// core_product_bundles already exists (built for inventory serial
// tracking) and is reused here purely for display; nothing about the
// order's actual stored total changes.
async function expandBundleLineItems(
  lineItems: InvoiceLineItem[],
  storedSubtotal: number,
): Promise<{ items: InvoiceLineItem[]; extraSubtotal: number; extraDiscount: number }> {
  const bundleSkus = [...new Set(lineItems.map((i) => i.sku).filter((s): s is string => !!s))];
  if (bundleSkus.length === 0) return { items: lineItems, extraSubtotal: 0, extraDiscount: 0 };

  const { data } = await supabase
    .from('core_product_bundles_public_v')
    .select('bundle_sku, component_sku, qty_per_bundle, component_name_en, component_msrp_bdt')
    .in('bundle_sku', bundleSkus);

  const componentsByBundle = new Map<string, BundleComponent[]>();
  for (const row of (data ?? []) as { bundle_sku: string; component_sku: string; qty_per_bundle: number; component_name_en: string | null; component_msrp_bdt: number | null }[]) {
    const list = componentsByBundle.get(row.bundle_sku) ?? [];
    list.push({ sku: row.component_sku, name_en: row.component_name_en ?? row.component_sku, msrp_bdt: Number(row.component_msrp_bdt ?? 0), qty_per_bundle: row.qty_per_bundle });
    componentsByBundle.set(row.bundle_sku, list);
  }
  // Set parts in routine order (Booster, Serum, Cream), not DB order.
  for (const [k, list] of componentsByBundle) componentsByBundle.set(k, sortProducts(list));
  if (componentsByBundle.size === 0) return { items: lineItems, extraSubtotal: 0, extraDiscount: 0 };

  let rawItemsTotal = 0;
  let totalDelta = 0;
  const items: InvoiceLineItem[] = [];
  for (const item of lineItems) {
    rawItemsTotal += item.amount;
    const components = item.sku ? componentsByBundle.get(item.sku) : undefined;
    if (!components || components.length === 0) {
      items.push(item);
      continue;
    }
    let componentsTotal = 0;
    for (const c of components) {
      const qty = c.qty_per_bundle * item.quantity;
      const amount = c.msrp_bdt * qty;
      componentsTotal += amount;
      items.push({ sku: c.sku, description: c.name_en ?? c.sku, quantity: qty, unit_price: c.msrp_bdt, amount });
    }
    totalDelta += componentsTotal - item.amount;
  }
  // core_create_order/core_update_order now bake this same bundle uplift
  // into the stored subtotal/discount_bdt directly (see
  // 20260811100000_bundle_aware_invoice_subtotal.sql) — adding it again
  // here would double-count for invoices written after that fix. Only
  // older invoices, where the stored subtotal still equals the raw
  // (unexpanded) line items sum, still need it added at display time.
  const alreadyApplied = storedSubtotal - rawItemsTotal;
  const extra = Math.max(0, totalDelta - alreadyApplied);
  return { items, extraSubtotal: extra, extraDiscount: extra };
}

async function mapInvoiceRow(r: InvoiceRequestRow, orderId: string): Promise<InvoiceForOrder> {
  // select('*') on purpose, not a named column list: PostgREST fails the
  // WHOLE query if even one named column doesn't exist on the live table
  // (found the hard way — challan_authorized_person_name's migration had
  // never actually been applied to production, so this query 400'd
  // silently and every invoice fell back to defaults, not just
  // challan-related fields). '*' degrades gracefully to whatever columns
  // actually exist.
  const { data: tplData, error: tplError } = await supabase.from('invoice_templates').select('*').eq('doc_type', r.doc_type).maybeSingle();
  // A failed fetch (e.g. RLS, or .maybeSingle() erroring on more than one
  // row for this doc_type) used to silently fall through to the `?? true`
  // defaults below — invisibly showing the VAT figure the admin had turned
  // off. Surface it, and fail toward NOT showing the figure instead.
  if (tplError) console.error('[mapInvoiceRow] invoice_templates fetch failed:', tplError.message);
  const t = (tplData as InvoiceTemplateRow | null) ?? null;
  const { items: expandedItems, extraSubtotal, extraDiscount } = await expandBundleLineItems(r.line_items ?? [], Number(r.subtotal ?? 0));
  const lineItems = await attachSerials(orderId, expandedItems);

  // VAT is read live from Settings -> Invoice Identity's current default
  // rate, not the rate frozen onto this invoice_requests row at
  // creation/last-edit time — matches erp-thekseoul's own Invoices view
  // (same back-out formula), so the same document shows the same rate
  // everywhere regardless of when it was originally issued.
  const finalSubtotal = Number(r.subtotal ?? 0) + extraSubtotal;
  const liveTaxRate = Number(t?.default_tax_rate) || 0;
  const liveTaxAmount = liveTaxRate > 0 ? round2(finalSubtotal - finalSubtotal / (1 + liveTaxRate / 100)) : 0;

  return {
    status: r.status,
    request: {
      doc_type: r.doc_type,
      doc_number: r.doc_number,
      issue_date: r.issue_date,
      due_date: r.due_date,
      currency: r.currency ?? 'BDT',
      recipient_name: r.recipient_name,
      recipient_email: r.recipient_email,
      company_name: r.company_name,
      business_number: r.business_number,
      company_address: r.company_address,
      company_contact: r.company_contact,
      line_items: lineItems,
      subtotal: finalSubtotal,
      discount_bdt: Number(r.discount_bdt ?? 0) + extraDiscount,
      shipping_bdt: Number(r.shipping_bdt ?? 0),
      tax_rate: liveTaxRate,
      tax_amount: liveTaxAmount,
      total_amount: Number(r.total_amount ?? 0),
      notes: r.notes,
      payment_terms: r.payment_terms,
    },
    template: {
      issuer_name: t?.issuer_name ?? 'The.K Seoul',
      office_address: t?.office_address ?? null,
      legal_entity_name: t?.legal_entity_name ?? DEFAULT_LEGAL_IDENTITY.legal_entity_name,
      concern_line: t?.concern_line ?? null,
      issuer_business_number: t?.issuer_business_number ?? DEFAULT_LEGAL_IDENTITY.issuer_business_number,
      trade_license_no: t?.trade_license_no ?? DEFAULT_LEGAL_IDENTITY.trade_license_no,
      issuer_address: t?.issuer_address ?? DEFAULT_LEGAL_IDENTITY.issuer_address,
      issuer_contact: t?.issuer_contact ?? null,
      issuer_email: t?.issuer_email ?? null,
      issuer_website: t?.issuer_website ?? null,
      logo_url: null,
      signature_url: null,
      stamp_url: null,
      watermark_url: null,
      social_url: t?.social_url ?? null,
      show_vat_amount: t?.show_vat_amount ?? false,
      text_overrides: t?.invoice_text_overrides ?? null,
    },
    challanAuthorizedPersonName: t?.challan_authorized_person_name ?? null,
    challanAuthorizedPersonDesignation: t?.challan_authorized_person_designation ?? null,
    challanTextOverrides: t?.challan_text_overrides ?? null,
  };
}

// Customer-facing: every order auto-generates its invoice as soon as it's
// placed ('issued'); 'sent' just means staff additionally emailed a copy —
// neither status gates the customer viewing their own invoice here.
export async function fetchInvoiceForOrder(orderId: string): Promise<InvoiceForOrder | null> {
  const { data: reqData } = await supabase
    .from('invoice_requests')
    .select(INVOICE_COLUMNS)
    .eq('order_id', orderId)
    .in('status', ['issued', 'sent'])
    .maybeSingle();

  const r = reqData as InvoiceRequestRow | null;
  if (!r) return null;
  return mapInvoiceRow(r, orderId);
}

// Staff-facing (RxK admin): any status — relies on invoice_requests'
// existing admin-read-all RLS policy rather than a status filter here.
export async function fetchInvoiceForOrderAdmin(orderId: string): Promise<InvoiceForOrder | null> {
  const { data: reqData } = await supabase.from('invoice_requests').select(INVOICE_COLUMNS).eq('order_id', orderId).maybeSingle();
  const r = reqData as InvoiceRequestRow | null;
  if (!r) return null;
  return mapInvoiceRow(r, orderId);
}

export interface FullInvoiceForOrderAdmin {
  template: InvoiceTemplateData;
  request: InvoiceRequestData;
  payment: InvoicePayment | undefined;
  salesPerson: string | null;
  soldBy: string | null;
  challanTemplate: DeliveryChallanTemplateData;
  receivedBy: { contactPerson: string | null; designation: string | null };
  refund: InvoiceRefund | null;
  cancelled: boolean;
  cancelReason: string | null;
}

// Staff-facing, everything needed to render+print either document format
// (InvoiceDocument or ReceiptDocument) for a given order — asset signed
// URLs, Sales Person, and Payment Status, on top of what
// fetchInvoiceForOrderAdmin returns. Used by both the admin invoice page and
// the Orders list's inline print picker, so the two stay in sync.
export async function fetchFullInvoiceForOrderAdmin(orderId: string): Promise<FullInvoiceForOrderAdmin | null> {
  const result = await fetchInvoiceForOrderAdmin(orderId);
  if (!result) return null;

  const { data, error: assetsError } = await supabase.functions.invoke('get-invoice-assets', { body: { order_id: orderId } });
  if (assetsError || !data?.ok) {
    // Logo/signature/stamp/watermark silently disappear otherwise — this at
    // least makes a asset-fetch failure (auth, CORS, missing invoice row)
    // visible in the console instead of just an unexplained blank header.
    console.error('get-invoice-assets failed:', assetsError ?? data);
  }
  const template: InvoiceTemplateData = {
    ...result.template,
    logo_url: data?.ok ? (data.logo_url ?? null) : null,
    signature_url: data?.ok ? (data.signature_image_url ?? null) : null,
    stamp_url: data?.ok ? (data.stamp_image_url ?? null) : null,
    watermark_url: data?.ok ? (data.watermark_image_url ?? null) : null,
  };

  const { data: order } = await supabase.from('core_orders').select('status, payment_method, confirmed_by_name, partner_id, channel, seller_ref, cancellation_reason').eq('id', orderId).maybeSingle();
  const salesPerson = order?.confirmed_by_name ?? null;
  const soldBy = order?.channel === 'pickk_seller' || order?.channel === 'pickk_gb' ? order?.seller_ref ?? null : null;
  const { data: shipments } = await supabase
    .from('core_shipments')
    .select('cod_status, cod_amount_bdt, delivered_at')
    .eq('order_id', orderId)
    .order('created_at', { ascending: false })
    .limit(1);
  const shipment = shipments?.[0];
  const { data: gateways } = await supabase.from('core_payment_gateways').select('gateway_key, display_name');
  const gatewayNames = Object.fromEntries(((gateways as { gateway_key: string; display_name: string }[]) ?? []).map((g) => [g.gateway_key, g.display_name]));
  const payment = order
    ? buildInvoicePaymentInfo(order.status, order.payment_method, shipment?.cod_amount_bdt ?? null, shipment?.delivered_at ?? null, gatewayNames)
    : undefined;

  // "Received By" on the Delivery Challan prefers the linked reseller/
  // partner's own contact person — falling back to just the order's
  // customer name (no designation line, see DeliveryChallanDocument) for a
  // regular retail order, which has no partner_id at all.
  let receivedBy: { contactPerson: string | null; designation: string | null } = { contactPerson: null, designation: null };
  if (order?.partner_id) {
    const { data: partner } = await supabase.from('core_partners').select('contact_person, designation').eq('id', order.partner_id).maybeSingle();
    receivedBy = { contactPerson: partner?.contact_person ?? null, designation: partner?.designation ?? null };
  }

  // Approved refunds stamp "REFUNDED" on the invoice/challan. Exchanges
  // don't — the customer still paid for (and kept) a product. Read live
  // on every load, so a refund approved after the invoice was issued
  // still shows up the next time it's opened or printed.
  const [{ data: refunds }, { data: orderItems }] = await Promise.all([
    supabase.from('core_returns').select('qty, reason, updated_at').eq('order_id', orderId).eq('type', 'refund').in('status', ['approved', 'completed']),
    supabase.from('core_order_items').select('qty').eq('order_id', orderId),
  ]);
  const refundRows = ((refunds as { qty: number | null; reason: string | null; updated_at: string }[]) ?? []).sort((a, b) => a.updated_at.localeCompare(b.updated_at));
  const refund: InvoiceRefund | null =
    refundRows.length > 0
      ? {
          qty: refundRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0),
          orderQty: ((orderItems as { qty: number }[]) ?? []).reduce((sum, i) => sum + (Number(i.qty) || 0), 0),
          refundedAt: refundRows.at(-1)?.updated_at ?? null,
          reason: refundRows.at(-1)?.reason ?? null,
        }
      : null;

  // Delivery Challan reuses this exact same branded 'invoice' identity
  // (logo/issuer/legal info) — no separate template row, just its own
  // pair of "Handed Over By" fields set once in Settings (erp-thekseoul).
  const challanTemplate: DeliveryChallanTemplateData = {
    issuer_name: template.issuer_name,
    concern_line: template.concern_line,
    issuer_business_number: template.issuer_business_number,
    trade_license_no: template.trade_license_no,
    issuer_address: template.issuer_address,
    issuer_contact: template.issuer_contact,
    issuer_email: template.issuer_email,
    issuer_website: template.issuer_website,
    logo_url: template.logo_url,
    watermark_url: template.watermark_url,
    social_url: template.social_url,
    challan_authorized_person_name: result.challanAuthorizedPersonName,
    challan_authorized_person_designation: result.challanAuthorizedPersonDesignation,
    text_overrides: result.challanTextOverrides,
  };

  return { template, request: result.request, payment, salesPerson, soldBy, challanTemplate, receivedBy, refund, cancelled: order?.status === 'cancelled', cancelReason: order?.cancellation_reason ?? null };
}

export async function fetchInvoiceAssets(orderId: string, customerAccessToken: string): Promise<InvoiceAssets | null> {
  if (!CORE_URL || !CORE_ANON) return null;
  const endpoint = `${CORE_URL.replace(/\/+$/, '')}/functions/v1/get-invoice-assets`;
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: CORE_ANON,
        Authorization: `Bearer ${customerAccessToken}`,
      },
      body: JSON.stringify({ order_id: orderId }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      logoUrl: data.logo_url ?? null,
      signatureImageUrl: data.signature_image_url ?? null,
      stampImageUrl: data.stamp_image_url ?? null,
      watermarkUrl: data.watermark_image_url ?? null,
    };
  } catch {
    return null;
  }
}
